import { Component, ElementRef, forwardRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
  selector: 'app-intake-form-input-date',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './intake-form-input-date.component.html',
  styleUrl: './intake-form-input-date.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntakeFormInputDateComponent),
      multi: true,
    },
  ],
})
export class IntakeFormInputDateComponent implements ControlValueAccessor, OnDestroy, OnInit {
  @ViewChild('month') monthInput: HTMLInputElement;
  @ViewChild('day') dayInput: HTMLInputElement;
  @ViewChild('year') yearInput: HTMLInputElement;
  private focusMonitor = inject(FocusMonitor);
  private elementRef = inject(ElementRef<HTMLElement>);
  private formBuilder = inject(FormBuilder);

  @Input()
  onKeydownEnter: () => void = () => {};

  public onChange: (value: string) => void;
  public onTouched: () => void;

  valueStore: FormGroup;

  ngOnInit() {
    this.valueStore = this.formBuilder.group({
      day: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(2)]],
      month: [
        '',
        [Validators.required, Validators.minLength(1), Validators.maxLength(2), Validators.min(1), Validators.max(12)],
      ],
      year: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
    });
  }

  ngOnDestroy() {
    this.focusMonitor.stopMonitoring(this.elementRef);
  }

  handleInput(event: InputEvent, control: AbstractControl<string>, nextElement?: HTMLInputElement) {
    if (event.data && (event.data.charCodeAt(0) < 48 || event.data.charCodeAt(0) > 57)) {
      control.setValue(control.value.substring(0, control.value.length - 1), { emitEvent: false });
    }
    if (nextElement && control.value.length >= 2) {
      this.focusNext(nextElement);
    }
    this.onChange(this.value);
  }

  handleLostFocus(control: AbstractControl<string>, min: number, max: number): void {
    let value = control.value;
    if (value.length < 2) {
      value = `0${value}`;
    }
    if (Number.parseInt(value, 10) > max) {
      value = max.toString();
    }
    if (Number.parseInt(value, 10) < min) {
      value = min.toString();
    }
    control.setValue(value, { emitEvent: false });
  }

  focusNext(nextElement: HTMLInputElement) {
    this.focusMonitor.focusVia(nextElement, 'program');
  }

  get value(): string {
    return Object.values(this.valueStore.value).toReversed().join('-');
  }

  writeValue(value: string) {
    const parts = value.split('-');
    this.valueStore.patchValue(
      {
        day: parts[0],
        month: parts[1],
        year: parts[2],
      },
      { emitEvent: false },
    );
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
